import { useEffect, useState } from "react";
import { AnimatePresence } from "framer-motion";
import { EditIcon, Plus, RefreshCcw, Trash2 } from "lucide-react";
import ConfirmationModal from "../../components/ConfirmationModal";
import toast from "react-hot-toast";
import AddRequirement from "./AddRequirement";
import EditRequirement from "./EditRequirement";
import SearchBar from "../../components/SearchBar";

export default function ManageRequirement({ onClose }) {
  const [requirements, setRequirements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [selectedRequirement, setSelectedRequirement] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [tags, setTags] = useState([]);

  const loopuserData = localStorage.getItem('loopuser');
  const loopUserObject = JSON.parse(loopuserData);

  // Fetch all tags
  const fetchTags = async () => {
    try {
      setLoading(true);
      const response = await fetch("https://loopback-skci.onrender.com/api/helper/allothertags", {
        method: "GET",
        headers: { "Content-Type": "application/json" },
      });
      const data = await response.json();
      if (data.status) {
        setTags(data.data);
      } else {
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTags();
  }, []);

  // Fetch all requirements
  const fetchRequirements = async () => {
    try {
      setLoading(true);
      const response = await fetch("https://loopback-skci.onrender.com/api/helper/allrequirements", {
        method: "GET",
        headers: { "Content-Type": "application/json" },
      });
      const data = await response.json();
      if (data.status) {
        setRequirements(data.data);
      } else {
        toast.error(data.message || "Failed to fetch requirements");
      }
    } catch (e) {
      console.error(e);
      toast.error("Error fetching requirements");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRequirements();
  }, []);

  const handleDelete = async () => {
    if (!selectedRequirement) {
      toast.error("Please select a requirement to delete");
      return;
    }
    try {
      const response = await fetch(
        `https://loopback-skci.onrender.com/api/helper/requirement/delete/${selectedRequirement.id}`,
        {
          method: "DELETE",
          headers: { "Content-Type": "application/json" },
        }
      );
      const data = await response.json();
      if (data.status) {
        toast.success("Requirement deleted!");
        fetchRequirements();
        setDeleteOpen(false);
      } else {
        toast.error(data.message || "Failed to delete requirement");
      }
    } catch (e) {
      console.error(e);
      toast.error("Error deleting requirement");
    }
  };

  return (
    <div className="">
      {/* Header */}
      <div className="flex items-center justify-end gap-2 bg-white p-3 rounded">
        <h2 className="text-[16px] font-semibold">Manage Services</h2>
        <div className="flex items-center gap-2">
          <button
            className="bg-gray-50 hover:bg-gray-200 text-gray-700 px-2 py-1.5 rounded text-[13px] font-medium transition-colors duration-200 flex items-center gap-1 leading-none"
            onClick={fetchRequirements}
          >
            <RefreshCcw size={11} className="" />
          </button>
          {(loopUserObject.fld_admin_type === "SUPERADMIN" || loopUserObject.fld_admin_type === "SUBADMIN") && (
            <button
              className="bg-green-600 hover:bg-green-700 text-white px-2 py-1.5 rounded text-[13px] font-medium transition-colors duration-200 flex items-center gap-1 leading-none"
              onClick={() => setAddOpen(true)}
            >
              Add<Plus size={11} className="" />
            </button>
          )}
        </div>
      </div>


      {/* Actions */}
      <div className="bg-white w-full f-13  p-3 rounded mt-3">


        {/* Table */}
        {loading ? (
          <div className="flex justify-center">
            <p className="text-center text-[13px] text-gray-500 flex items-center gap-2 ">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="50"
                height="50"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#f16639"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
                class="infinity"
              >
                <path d="M6 16c5 0 7-8 12-8a4 4 0 0 1 0 8c-5 0-7-8-12-8a4 4 0 1 0 0 8" />
              </svg>
              Loading services...
            </p>
          </div>

        ) : requirements.length === 0 ? (
          <p className="text-center text-[13px] text-gray-500">No services found.</p>
        ) : (
          <div className="overflow-x-auto">
            <div className="flex items-end justify-end gap-2 pb-3">
              <SearchBar
                value={searchTerm}
                onChange={setSearchTerm}
                placeholder="Search by Name"
              />
            </div>
            <table className="min-w-full text-[13px] border border-gray-200">
              <thead className="bg-[#e4eaff]">
                <tr>
                  <th className="px-4 py-2 text-left border border-[#ccc]">Name</th>
                  <th className="px-4 py-2 text-left border border-[#ccc]">Actions</th>
                </tr>
              </thead>
              <tbody>
                {requirements
                  .filter((requirement) =>
                    requirement.category
                      .toLowerCase()
                      .includes(searchTerm.toLowerCase()) ||
                    requirement.name
                      .toLowerCase()
                      .includes(searchTerm.toLowerCase())
                  )
                  .map((requirement, idx) => (
                    <tr key={requirement.id || idx} className="border-t hover:bg-gray-50">
                      <td className="px-4 py-2 border border-[#ccc]">{requirement.name}</td>
                      <td className="px-4 py-2 border border-[#ccc]">
                        <div className="flex items-center space-x-2">
                          {(loopUserObject.fld_admin_type === "SUPERADMIN" || loopUserObject.fld_admin_type === "SUBADMIN") && (
                            <button
                              className="edit-btn bg-yellow-500 hover:bg-yellow-600 text-white px-2 py-1 rounded text-xs font-medium transition-colors duration-200 flex items-center gap-1"
                              onClick={() => {
                                setSelectedRequirement(requirement);
                                setEditOpen(true);
                              }}
                            >
                              <EditIcon size={13} />
                            </button>
                          )}
                          {(loopUserObject.fld_admin_type === "SUPERADMIN" || loopUserObject.fld_admin_type === "SUBADMIN") && (
                            <button
                              className="delete-btn bg-red-500 hover:bg-red-600 text-white px-2 py-1 rounded text-xs font-medium transition-colors duration-200 flex items-center gap-1"
                              onClick={() => {
                                setSelectedRequirement(requirement);
                                setDeleteOpen(true);
                              }}
                            >
                              <Trash2 size={13} />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modals */}
      <AnimatePresence>
        {addOpen && (
          <AddRequirement
            onClose={() => setAddOpen(false)}
            after={fetchRequirements}
            tags={tags}
          />
        )}
        {editOpen && (
          <EditRequirement
            onClose={() => setEditOpen(false)}
            requirementData={selectedRequirement}
            onUpdate={fetchRequirements}
            tags={tags}
          />
        )}
        {deleteOpen && (
          <ConfirmationModal
            title="Are you sure you want to delete this service?"
            message="This action is irreversible."
            onYes={handleDelete}
            onClose={() => setDeleteOpen(false)}
          />
        )}
      </AnimatePresence>
    </div>
  );
}
